import React, { useEffect, useState } from 'react';
import { Box, Button, TextField, Typography, Alert, Card, CardContent, Stack, Chip, Divider } from '@mui/material';
import { Business } from '@mui/icons-material';
import { tenantService } from '../services/api';

// A company's own Admin edits their company profile and branding here. The plan, employee limit and
// expiry are shown read-only: only the platform operator can change them (the API has no field for
// them on this screen's endpoint).

const CompanySettings: React.FC = () => {
  const [tenant, setTenant] = useState<any>(null);
  const [form, setForm] = useState({
    description: '', contactEmail: '', contactPhone: '', companyAddress: '', companyWebsite: '',
    logoUrl: '', primaryColor: '#1976d2', secondaryColor: '#dc004e',
  });
  const [message, setMessage] = useState<{ text: string; severity: 'success' | 'error' } | null>(null);

  useEffect(() => {
    tenantService.getCurrent().then((t) => {
      setTenant(t);
      setForm({
        description: t.description || '', contactEmail: t.contactEmail || '', contactPhone: t.contactPhone || '',
        companyAddress: t.companyAddress || '', companyWebsite: t.companyWebsite || '', logoUrl: t.logoUrl || '',
        primaryColor: t.primaryColor || '#1976d2', secondaryColor: t.secondaryColor || '#dc004e',
      });
    }).catch(() => setMessage({ text: 'Could not load your company', severity: 'error' }));
  }, []);

  const set = (field: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm({ ...form, [field]: e.target.value });

  const save = async () => {
    try {
      await tenantService.updateCurrent(form);
      setMessage({ text: 'Company settings saved', severity: 'success' });
    } catch (error: any) {
      setMessage({ text: error.response?.data?.message || 'Could not save', severity: 'error' });
    }
  };

  if (!tenant) return <Box sx={{ p: 3 }}>{message ? <Alert severity={message.severity}>{message.text}</Alert> : 'Loading…'}</Box>;

  const limit = tenant.maxEmployees > 0 ? tenant.maxEmployees : '∞';

  return (
    <Box sx={{ p: 3, maxWidth: 800 }}>
      <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
        <Business sx={{ mr: 1, verticalAlign: 'middle' }} />
        {tenant.name}
      </Typography>

      {message && <Alert severity={message.severity} sx={{ mb: 2 }} onClose={() => setMessage(null)}>{message.text}</Alert>}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Chip label={`Plan: ${tenant.subscriptionPlan}`} />
            <Chip label={`Employees: ${tenant.activeUsers} / ${limit}`} color={tenant.maxEmployees > 0 && tenant.activeUsers >= tenant.maxEmployees ? 'warning' : 'default'} />
            <Chip label={tenant.subscriptionEndDate ? `Valid through ${new Date(tenant.subscriptionEndDate).toLocaleDateString()}` : 'Never expires'} />
            {tenant.subdomain && <Chip label={`Subdomain: ${tenant.subdomain}`} variant="outlined" />}
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            To change your plan or employee limit, contact the platform administrator.
          </Typography>
        </CardContent>
      </Card>

      <Stack spacing={2}>
        <TextField fullWidth label="Description" value={form.description} onChange={set('description')} multiline rows={2} />
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
          <TextField fullWidth label="Contact Email" type="email" value={form.contactEmail} onChange={set('contactEmail')} />
          <TextField fullWidth label="Contact Phone" value={form.contactPhone} onChange={set('contactPhone')} />
        </Box>
        <TextField fullWidth label="Company Address" value={form.companyAddress} onChange={set('companyAddress')} />
        <TextField fullWidth label="Company Website" value={form.companyWebsite} onChange={set('companyWebsite')} />
        <Divider textAlign="left"><Typography variant="caption" color="text.secondary">Branding</Typography></Divider>
        <TextField fullWidth label="Logo URL" value={form.logoUrl} onChange={set('logoUrl')} helperText="A public https:// link to your logo image" />
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
          <TextField fullWidth label="Primary Color" type="color" value={form.primaryColor} onChange={set('primaryColor')} />
          <TextField fullWidth label="Secondary Color" type="color" value={form.secondaryColor} onChange={set('secondaryColor')} />
        </Box>
        <Box><Button variant="contained" onClick={save}>Save</Button></Box>
      </Stack>
    </Box>
  );
};

export default CompanySettings;
